import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSubcontractsStore } from '@/stores/subcontracts'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      // La raíz manda a cada usuario a su portal según el ámbito de su rol.
      path: '/',
      redirect: () => {
        const authStore = useAuthStore()
        return authStore.isAdmin ? '/admin' : '/supplier'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: {
        requiresGuest: true
      }
    },
    {
      path: '/supplier',
      name: 'supplier',
      component: () => import('@/views/SupplierView.vue'),
      meta: {
        requiresAuth: true,
        title: 'Portal de Proveedores'
      }
    },
    {
      path: '/supplier/subcontratos',
      name: 'supplier-subcontracts',
      component: () => import('@/views/SubcontractsView.vue'),
      meta: {
        requiresAuth: true,
        requiresModule: 'subcontracts',
        requiresIntegration: 'subcontracts',
        title: 'Mis subcontratos'
      }
    },
    {
      path: '/supplier/subcontratos/:id',
      name: 'supplier-subcontract-detail',
      component: () => import('@/views/SubcontractDetailView.vue'),
      meta: {
        requiresAuth: true,
        requiresModule: 'subcontracts',
        requiresIntegration: 'subcontracts',
        title: 'Subcontrato'
      }
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: () => import('@/views/ChangePasswordView.vue'),
      meta: {
        requiresAuth: true,
        title: 'Cambiar Contraseña'
      }
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/AdminView.vue'),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        title: 'Dashboard Administrativo'
      }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue')
    }
  ]
})

// El rol puede tener el módulo y el backend tenerlo apagado: sin
// CONTRATOS_API_URL el Portal de Compras no responde y /subcontracts/status
// contesta enabled: false.
const subcontractsEnabled = async () => {
  const subcontractsStore = useSubcontractsStore()

  if (subcontractsStore.enabled === null) {
    return await subcontractsStore.fetchStatus()
  }

  return subcontractsStore.enabled
}

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (to.meta.title) {
    document.title = `${to.meta.title} - Portal de Proveedores`
  } else {
    document.title = 'Portal de Proveedores'
  }

  if (!authStore.initialized) {
    await authStore.initializeAuth()
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Forzar cambio de contraseña si es primer inicio
  if (authStore.isAuthenticated && authStore.user?.mustChangePassword && to.name !== 'change-password') {
    next('/change-password')
    return
  }

  // El portal admin exige un rol de ámbito 'admin' (no un nombre de rol concreto)
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next('/supplier')
    return
  }

  if (to.meta.requiresModule && !authStore.canAccessModule(to.meta.requiresModule)) {
    next('/')
    return
  }

  if (to.meta.requiresIntegration === 'subcontracts' && !(await subcontractsEnabled())) {
    next('/')
    return
  }

  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next(authStore.isAdmin ? '/admin' : '/supplier')
    return
  }

  next()
})

export default router